import React, { useState, useRef, useEffect } from 'react';
import Slider from '@mui/material/Slider';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import { WarningCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { SkillValidationError } from 'talent_matching/types';
import Container from '@grnhse/seedling/lib/birch/components/container';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_matching.modals.calibration');

type Props = {
  value: number;
  name: string;
  onRemove: (value: string) => void;
  onChange: (value: number) => void;
  validationError?: SkillValidationError;
  disabled?: boolean;
};

const importanceLabelMap: Record<number, string> = {
  1: t('importance.not_very_important'),
  2: t('importance.somewhat_important'),
  3: t('importance.important'),
  4: t('importance.very_important'),
  5: t('importance.crucial'),
};

const StyledSlider = styled(Slider)<{
  trackColor: string;
  thumbColor: string;
}>`
  margin-top: 10px;

  & .MuiSlider-thumb {
    background-color: ${(props) => props.thumbColor};
    height: 12px;
    width: 12px;
    border-radius: 50%;
  }
  & .MuiSlider-rail {
    background-color: ${FullPalette.GREY_700};
    border: 1px solid ${FullPalette.GREY_800};
    height: 6px;
    margin-left: -${Tokens.spacing['0.5']};
    padding-right: ${Tokens.spacing[1]};
  }
  & .MuiSlider-mark {
    background-color: ${FullPalette.GREY_800};
    width: 3px;
    height: 3px;
    border-radius: 50%;
  }
  & .MuiSlider-track {
    background-color: ${(props) => props.trackColor};
    height: 4px;
    border-color: ${FullPalette.GREY_800};
    margin-left: -${Tokens.spacing['0.5']};
  }
`;

const trackColorMap: Record<number, string> = {
  1: FullPalette.GREEN_E50,
  2: FullPalette.GREEN_E100,
  3: FullPalette.GREEN_E300,
  4: FullPalette.GREEN_500,
  5: FullPalette.GREEN_700,
};

// Due to some weird issues with the Grid component, we need to set a
// fixed height to ensure the spacing after each slider component is consistent.
const StyledContainer = styled(Container)<{
  hasError: boolean;
  isWrapped: boolean;
}>`
  height: ${(props) => {
    if (props.hasError) {
      return '140px';
    }
    if (props.isWrapped) {
      return '72px';
    }
    return '48px';
  }};
  padding: 0 16px;
  align-content: center;
`;

const TruncatedBody = styled(Body)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TruncatedBodyWithTooltip = ({
  text,
  onWrapChange,
}: {
  text: string;
  onWrapChange: (isWrapped: boolean) => void;
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      const wrapper = wrapperRef.current;
      // We get the ref to the span, then check its first child, which is the TruncatedBody's p tag.
      // This is a bit of a hack, but it's the only way to get the actual height of the text
      // since we aren't forwarding the ref.
      if (wrapper && wrapper.firstChild) {
        const element = wrapper.firstChild as HTMLElement;
        const isWrapped = element.scrollHeight >= 25;
        onWrapChange(isWrapped);
        const isNowTruncated = element.scrollHeight > element.clientHeight;
        if (isNowTruncated !== isTruncated) {
          setIsTruncated(isNowTruncated);
        }
      }
    };

    // Debounce the check to wait for render to complete
    const timeoutId = setTimeout(checkTruncation, 100);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text, isTruncated, onWrapChange]);

  const label = isTruncated ? text : undefined;

  return (
    <Tooltip label={label} ariaLabel={text}>
      <span ref={wrapperRef}>
        <TruncatedBody>{text}</TruncatedBody>
      </span>
    </Tooltip>
  );
};

const SliderComponent = ({
  value,
  name,
  onRemove,
  onChange,
  validationError,
  disabled = false,
}: Props) => {
  const [isNameWrapped, setIsNameWrapped] = useState(false);
  const [isLabelWrapped, setIsLabelWrapped] = useState(false);
  const [label, setLabel] = useState(() => {
    return importanceLabelMap[value];
  });

  const labelWrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const checkWrapper = () => {
      const wrapper = labelWrapperRef.current;
      if (wrapper && wrapper.firstChild) {
        const element = wrapper.firstChild as HTMLElement;
        const isWrapped = element.scrollHeight >= 25;
        setIsLabelWrapped(isWrapped);
      }
    };
    // Debounce the check to wait for render to complete
    const timeoutId = setTimeout(checkWrapper, 100);
    window.addEventListener('resize', checkWrapper);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkWrapper);
    };
  }, [label, isLabelWrapped, labelWrapperRef]);

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    if (disabled) {
      return;
    }
    setLabel(importanceLabelMap[newValue as number]);
    onChange(newValue as number);
  };

  const errorReason = validationError?.reason;

  return (
    <StyledContainer
      hasError={!!errorReason}
      isWrapped={isNameWrapped || isLabelWrapped}
    >
      <Stack spacing={1}>
        <Grid container>
          <Grid xs={4} alignContent="center">
            <TruncatedBodyWithTooltip
              text={name}
              onWrapChange={setIsNameWrapped}
            />
          </Grid>
          <Grid xs={3}>
            <StyledSlider
              aria-label="Skill importance"
              value={value}
              getAriaValueText={(val: number) => val.toString()}
              step={1}
              valueLabelDisplay="off"
              marks
              min={1}
              max={5}
              onChange={handleSliderChange}
              trackColor={trackColorMap[value]}
              thumbColor={
                disabled ? FullPalette.GREY_E500 : FullPalette.EVERGREEN_700
              }
              disabled={disabled}
            />
          </Grid>
          <Grid xs={4} alignContent="center">
            <span ref={labelWrapperRef}>
              <Body>{label}</Body>
            </span>
          </Grid>
          {!disabled && (
            <Grid xs={1} alignContent="center">
              <IconButton
                label={'remove-skill'}
                onClick={() => onRemove(name)}
                icon={CloseIcon}
                flat
              />
            </Grid>
          )}
        </Grid>
        {errorReason && <WarningCallOut bodyText={errorReason} />}
      </Stack>
    </StyledContainer>
  );
};

export default SliderComponent;
